/**
 * Coach API Hooks - TanStack Query hooks for coach-specific endpoints
 *
 * These hooks provide access to the coach attendance workflow endpoints
 * which are not part of the generated SDK.
 */

import { useQuery, useQueryClient } from "@tanstack/react-query"

import api from "@/services/api"
import type {
  BulkAttendanceRequest,
  BulkAttendanceResponse,
  SessionRosterResponse,
  TodaysClassesResponse,
} from "@/types/coach"
import type { StandardResponse } from "@/utils/apiResponse"
import { unwrapStandardResponse } from "@/utils/apiResponse"

import { useApiMutation } from "./useApiMutation"

const COACH_QUERY_KEYS = {
  todaysClasses: ["coach", "my-classes", "today"] as const,
  sessionRoster: (sessionId: string) =>
    ["coach", "session-roster", sessionId] as const,
}

/**
 * Fetch today's classes for the logged-in coach
 */
export function useTodaysClasses() {
  return useQuery({
    queryKey: COACH_QUERY_KEYS.todaysClasses,
    queryFn: async (): Promise<TodaysClassesResponse> => {
      const response = await api.instance.get<
        StandardResponse<TodaysClassesResponse>
      >("/api/v1/coach/my-classes/today")

      return unwrapStandardResponse<TodaysClassesResponse>(
        response.data,
      ) as TodaysClassesResponse
    },
    staleTime: 1 * 60 * 1000, // 1 minute - refresh frequently for attendance tracking
    gcTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Fetch the roster for a specific class session
 */
export function useSessionRoster(sessionId: string | null) {
  return useQuery({
    queryKey: sessionId
      ? COACH_QUERY_KEYS.sessionRoster(sessionId)
      : ["coach", "session-roster", "none"],
    queryFn: async (): Promise<SessionRosterResponse> => {
      if (!sessionId) {
        throw new Error("Session ID is required")
      }

      const response = await api.instance.get<
        StandardResponse<SessionRosterResponse>
      >(`/api/v1/coach/class-sessions/${sessionId}/roster`)

      return unwrapStandardResponse<SessionRosterResponse>(
        response.data,
      ) as SessionRosterResponse
    },
    enabled: !!sessionId,
    staleTime: 30 * 1000, // 30 seconds - refresh frequently during attendance
    gcTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Bulk mark attendance for a class session
 */
export function useBulkMarkAttendance() {
  const queryClient = useQueryClient()

  return useApiMutation<
    BulkAttendanceResponse,
    unknown,
    { sessionId: string; data: BulkAttendanceRequest }
  >({
    mutationFn: async ({ sessionId, data }) => {
      const response = await api.instance.post<
        StandardResponse<BulkAttendanceResponse>
      >(`/api/v1/coach/class-sessions/${sessionId}/attendance/bulk`, data)

      return unwrapStandardResponse<BulkAttendanceResponse>(
        response.data,
      ) as BulkAttendanceResponse
    },
    onSuccess: (_data, variables) => {
      // Invalidate the roster query to refresh attendance status
      void queryClient.invalidateQueries({
        queryKey: COACH_QUERY_KEYS.sessionRoster(variables.sessionId),
      })
      // Invalidate today's classes to update the progress counts
      void queryClient.invalidateQueries({
        queryKey: COACH_QUERY_KEYS.todaysClasses,
      })
      // Also invalidate the general attendances list
      void queryClient.invalidateQueries({
        queryKey: ["attendances"],
      })
    },
    successToast: (data) => ({
      title: "Attendance saved",
      description: `${data.successful} of ${data.processed} records saved successfully`,
    }),
  })
}

/**
 * Mark all students present in a session
 */
export function useMarkAllPresent() {
  return useBulkMarkAttendance()
}

/**
 * Invalidate coach queries
 */
export function useInvalidateCoachQueries() {
  const queryClient = useQueryClient()

  return {
    invalidateTodaysClasses: () => {
      void queryClient.invalidateQueries({
        queryKey: COACH_QUERY_KEYS.todaysClasses,
      })
    },
    invalidateSessionRoster: (sessionId: string) => {
      void queryClient.invalidateQueries({
        queryKey: COACH_QUERY_KEYS.sessionRoster(sessionId),
      })
    },
    invalidateAll: () => {
      void queryClient.invalidateQueries({
        queryKey: ["coach"],
      })
    },
  }
}
